import {ChangeDetectionStrategy, Component, computed, effect, input, OnDestroy, OnInit, signal} from '@angular/core';
import {SurveyMetrics} from '../../../../../../types/SurveyMetrics';
import {SurveyMetricDimension} from '../../../../../../types/SurveyMetricDimension';
import {Subscription} from 'rxjs';
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {ToolsService} from '../../../../../../services/tools.service';
import {ModalsControlService} from '../../../../../../services/modals-control.service';
import {InfoModalContent} from '../../../../../../types/InfoModalContent';
import {ModalCommand} from '../../../../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../../../../enums/ModalEnums';

@Component({
  selector: 'app-cpru-survey',
  templateUrl: './cpru-survey.component.html',
  styleUrl: './cpru-survey.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block md:rounded-lg md:border md:border-grey-9 md:bg-white md:px-4 md:py-5',
  },
})
export class CpruSurveyComponent implements OnInit, OnDestroy {
  public survey = input<SurveyMetrics>(null);
  public name = input<string>(null);
  public panelsState = signal<Array<boolean>>([]);
  public perRow = signal<number>(null);
  public rows = computed<Array<Array<SurveyMetricDimension>>>(() => {
    if (!this.survey()) {
      return [];
    }

    const rows: Array<Array<SurveyMetricDimension>> = [];
    let row = [];
    for (let i = 0; i < this.survey().dimensions.length; i++) {
      row.push(this.survey().dimensions[i]);
      if ((i + 1) % this.perRow() === 0) {
        rows.push(row);
        row = [];
      }
    }
    if (row.length > 0) {
      rows.push(row);
    }
    return rows;
  });
  private subscription: Subscription;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private tools: ToolsService,
    private modalControl: ModalsControlService,
  ) {
    effect(
      () => {
        this.panelsState.set(this.survey().dimensions.map(() => false));
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit() {
    this.perRow.set(this.breakpointObserver.isMatched(['(max-width: 768px)']) ? 2 : 3);
    this.subscription = this.breakpointObserver.observe(['(max-width: 768px)']).subscribe((bs: BreakpointState) => {
      this.perRow.set(bs.breakpoints['(max-width: 768px)'] ? 2 : 3);
    });
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public togglePanelState(ind: number, open: boolean) {
    this.panelsState.set(this.panelsState().map((s, i) => i === ind && open));

    if (!open) {
      this.tools.scrollTo(`survey-dimension-${ind}`, 400, -190);
    }
  }

  public openInfoModal(): void {
    const infoContent: InfoModalContent = {
      title: 'Nationella patientenkäten',
      message: `<p>Nationell Patientenkät är ett samlingsnamn för återkommande nationella patientenkäter som
samordnas av Sveriges Kommuner och Landsting (SKL). Målet med enkäterna är att fånga upp patienternas upplevelse
av vården och undersökningen baseras på att patienten får utvärdera sitt senaste besök hos vårdgivaren.
Resultatet används sedan för att utveckla och förbättra vården utifrån ett patientperspektiv, men även som
ett underlag för att göra jämförelser mellan vårdgivare och ett redskap för styrning och ledning.</p>

<p><b>Nationell Patientenkät genomförs vartannat år inom:</b></p>
<ul>
<li>Primärvård</li>
<li>Somatisk öppen- och slutenvård</li>
<li>Akutmottagningar</li>
<li>Psykiatrisk öppen- och slutenvård</li>
<li>Barnsjukvård, öppen- och slutenvård</li>
<li>Barn- och ungdomspsykiatri</li>
</ul>

<p>Enskilda landsting och regioner har även möjlighet att utföra extra mätningar inom dessa eller andra områden.</p>`,
      width: 720,
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: infoContent,
    };
    this.modalControl.openModal(modalCommand);
  }
}
